import { useHandler } from "@redotech/react-util/hook";
import { Step, StepId, StepType } from "@redotech/redo-return-ui/flows/step";
import { ReactFlowInstance } from "@xyflow/react";
import { Dispatch, SetStateAction } from "react";
import { handleStepDropped } from "../utils/palette/drop-handler";
import { FlowEditorDragType } from "../utils/palette/step-type-registry";
import { UndoRedoState } from "../utils/state/flow-editor-types";

export interface UseDragDropProps {
  reactFlowInstance: ReactFlowInstance | null;
  steps: Map<StepId, Step>;
  stepOptions: StepType<any, any>[];
  gridSize: number;
  setUndoRedoState: Dispatch<SetStateAction<UndoRedoState>>;
  setModified: (modified: boolean) => void;
  setSelectedParam: (id: string | undefined) => void;
}

export interface UseDragDropReturn {
  onDragOver: (event: React.DragEvent) => void;
  onDrop: (event: React.DragEvent) => void;
}

export function useDragDrop({
  reactFlowInstance,
  steps,
  stepOptions,
  gridSize,
  setUndoRedoState,
  setModified,
  setSelectedParam,
}: UseDragDropProps): UseDragDropReturn {
  const onDragOver = useHandler((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";

    if (!reactFlowInstance) {
      return;
    }

    let dragType = event.dataTransfer.getData(
      "application/reactflow",
    ) as FlowEditorDragType;
    if (!dragType) {
      dragType = event.dataTransfer.getData("text/plain") as FlowEditorDragType;
    }
    if (!dragType) {
      return;
    }
  });

  const onDrop = useHandler((event: React.DragEvent) => {
    event.preventDefault();

    if (!reactFlowInstance) {
      return;
    }

    let dragType = event.dataTransfer.getData(
      "application/reactflow",
    ) as FlowEditorDragType;
    if (!dragType) {
      dragType = event.dataTransfer.getData("text/plain") as FlowEditorDragType;
    }
    if (!dragType) {
      return;
    }

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    const centeredPosition = { x: position.x - 110, y: position.y - 50 };

    const result = handleStepDropped({
      dragType,
      position: centeredPosition,
      steps,
      stepOptions,
      gridSize,
    });

    if (result) {
      setUndoRedoState((prev) => {
        const updatedPositions = new Map(prev.userNodePositions);
        updatedPositions.set(result.newStepId, result.position);
        return {
          steps: result.steps,
          userNodePositions: updatedPositions,
          selectedId: result.newStepId,
        };
      });
      setModified(true);
      setSelectedParam(result.newStepId);
    }
  });

  return { onDragOver, onDrop };
}
